import { useCallback, useEffect, useRef, useState } from "react";
import {
  isValidIranianMobile,
  isValidNationalId,
  normaliseNationalId,
  type Gender,
} from "@talent/protocol";
import { AuthError, applyToTeach, requestCode, type AuthFailure } from "../auth-api.js";
import { COPY, fill } from "../copy.js";
import { Creature } from "../creature.js";
import { digitsOnly, fa } from "../format.js";
import { COLOR, css } from "../paint.js";
import { CODE_LENGTH, CodeBoxes, FIELD, LABEL } from "./auth-parts.js";

/**
 * Registering as a teacher. **Its own page, and it no longer looks like the sign-in card.**
 *
 * It used to be a wide version of the card sign-in uses, reached from a link at the bottom of that
 * card. Two things were wrong with that. A five-field form for teachers sat one tap underneath a phone
 * box that is mostly used by the parent of a five-year-old, so the way in read as a registration wall
 * for people with nothing to register. And a person handing over their national id could not see what
 * they were handing it to: the card had room for the fields and no room to say that an account is not
 * what comes back — a queue is, read by a person, with no date on it (ADR-012).
 *
 * So the page is built the other way round. The left column says who it is for and what happens after
 * the button; the right column is the form. On a phone that is one column in the order that matters —
 * what this is, then the form, then what happens next — and every field is reachable without the page
 * turning into a scroll of identical boxes.
 *
 * **One page, then the code.** The code is still a step of its own, and it has to be: it SUBMITS the
 * application, because a pending row must always correspond to somebody who held that number, or the
 * queue is a spam target and every row in it costs a person's attention to read.
 *
 * The national id is checked here as well as on the server, and the duplication is the point. The same
 * function from `@talent/protocol` answers both, so there is one definition of the rule; running it in
 * the browser means a mistyped digit is caught before a code is spent on a form that will be refused.
 */

const FAILURE_COPY: Record<AuthFailure, string> = {
  "bad-phone": COPY.enter.badPhone,
  "bad-code": COPY.enter.badCode,
  cooldown: COPY.enter.cooldown,
  quota: COPY.enter.quota,
  "sms-failed": COPY.enter.smsFailed,
  deleted: COPY.enter.deleted,
  "already-applied": COPY.apply.alreadyApplied,
  "national-id-taken": COPY.apply.nationalIdTaken,
  unknown: COPY.enter.failed,
};

interface Details {
  firstName: string;
  lastName: string;
  nationalId: string;
  schoolName: string;
  phone: string;
  /** Unanswered until it is answered. No default, because a default here is an answer nobody gave. */
  gender: Gender | null;
}

const EMPTY: Details = {
  firstName: "",
  lastName: "",
  nationalId: "",
  schoolName: "",
  phone: "",
  gender: null,
};

/* ── The page's own look ──────────────────────────────────────────────────────────────────────── */

/**
 * The action, sized for an adult's thumb on a phone.
 *
 * 60px rather than the 76px `min-h-touch` the games use: that token is for a five-year-old pressing a
 * tile with a whole hand, and a form whose every control is that tall stops looking like a form. The
 * tinted lift is what makes this read as the one thing on the card to press.
 */
const PRIMARY =
  "h-[60px] w-full rounded-pill bg-grape px-lg text-heading text-card shadow-tint-grape";

const QUIET = "min-h-11 w-full text-small text-muted";

export function ApplyScreen({ onSignIn }: { onSignIn: () => void }) {
  const [details, setDetails] = useState<Details>(EMPTY);
  const [step, setStep] = useState<"form" | "code" | "done">("form");
  const [code, setCode] = useState("");
  const [failure, setFailure] = useState<AuthFailure | null>(null);
  /** The one refusal this screen decides by itself, so it is not an `AuthFailure`. */
  const [badId, setBadId] = useState(false);
  const [badPhone, setBadPhone] = useState(false);
  const [busy, setBusy] = useState(false);

  const set = <K extends keyof Details>(key: K) => (value: Details[K]) =>
    setDetails((d) => ({ ...d, [key]: value }));

  const handleError = useCallback((err: unknown) => {
    setFailure(err instanceof AuthError ? err.failure : "unknown");
  }, []);

  const complete =
    details.firstName.trim() &&
    details.lastName.trim() &&
    details.schoolName.trim() &&
    details.gender !== null &&
    details.nationalId.length === 10 &&
    isValidIranianMobile(details.phone);

  /**
   * One button: it validates the id and the phone, then sends the code.
   *
   * Both checks happen here rather than on every keystroke, for the same reason: a check digit is
   * wrong for the first nine digits of every valid id, and a phone number is incomplete for every
   * digit typed before the last one, so validating as they type would show an error the entire way
   * through. Checked here as well as gating the button, because a text field submits on Enter
   * whether or not the button below it is disabled.
   */
  const sendCode = useCallback(
    async (e?: React.FormEvent) => {
      e?.preventDefault();
      if (busy) return;
      if (!isValidNationalId(details.nationalId)) {
        setBadId(true);
        return;
      }
      if (!isValidIranianMobile(details.phone)) {
        setBadPhone(true);
        return;
      }
      setBadId(false);
      setBadPhone(false);
      setBusy(true);
      setFailure(null);
      try {
        await requestCode(details.phone);
        setStep("code");
      } catch (err) {
        handleError(err);
      } finally {
        setBusy(false);
      }
    },
    [busy, details.nationalId, details.phone, handleError],
  );

  const submit = useCallback(
    async (e?: React.FormEvent) => {
      e?.preventDefault();
      if (busy) return;
      // Cannot happen from the form, which will not advance without one — but the type says it can, and
      // sending the request without it would spend the code on a body the server refuses.
      if (!details.gender) return;
      setBusy(true);
      setFailure(null);
      try {
        await applyToTeach({
          phone: details.phone,
          code,
          firstName: details.firstName.trim(),
          lastName: details.lastName.trim(),
          nationalId: normaliseNationalId(details.nationalId),
          schoolName: details.schoolName.trim(),
          gender: details.gender,
        });
        setStep("done");
      } catch (err) {
        // The code is spent whatever happens, so it is cleared: leaving it invites another press of
        // the button on six digits the server has already retired.
        setCode("");
        handleError(err);
      } finally {
        setBusy(false);
      }
    },
    [busy, code, details, handleError],
  );

  /** A full code submits the application, for the same reason it submits a sign-in. */
  const autoSubmitted = useRef(false);
  useEffect(() => {
    if (step !== "code") return;
    if (code.length < CODE_LENGTH) {
      autoSubmitted.current = false;
      return;
    }
    if (busy || autoSubmitted.current) return;
    autoSubmitted.current = true;
    void submit();
  }, [busy, code, step, submit]);

  const error = failure && (
    <p className="mt-md text-small text-wrong">{FAILURE_COPY[failure]}</p>
  );

  if (step === "done") {
    return (
      <Narrow>
        <Panel>
          <StepMark mark="done" />
          <h1 className="mt-lg text-title">{COPY.apply.done.title}</h1>
          <p className="mt-sm text-body text-muted">{COPY.apply.done.body}</p>
          <button type="button" onClick={onSignIn} className={`${PRIMARY} mt-lg`}>
            {COPY.apply.done.enter}
          </button>
        </Panel>
      </Narrow>
    );
  }

  if (step === "code") {
    const cannotSubmit = busy || code.length !== CODE_LENGTH;
    return (
      <Narrow>
        <Panel>
          <StepMark mark="code" />
          <h1 className="mt-lg text-title">{COPY.enter.codeTitle}</h1>
          <p className="mt-sm text-small text-muted">
            {fill(COPY.enter.codeSent, { phone: details.phone })}
          </p>

          <form onSubmit={submit}>
            <div className="mt-lg">
              <CodeBoxes value={code} onChange={setCode} />
            </div>
            {/* The one sentence this step must carry: the code is what files the application. */}
            <p className="mt-sm text-caption text-muted">{COPY.apply.codeIntro}</p>

            {error}

            <button
              type="submit"
              disabled={cannotSubmit}
              className={`${PRIMARY} mt-lg ${cannotSubmit ? "opacity-60" : ""}`}
            >
              {busy ? COPY.enter.working : COPY.apply.submit}
            </button>

            <button
              type="button"
              onClick={() => {
                setStep("form");
                setCode("");
                setFailure(null);
              }}
              className={`${QUIET} mt-md`}
            >
              {COPY.apply.editDetails}
            </button>
          </form>
        </Panel>
      </Narrow>
    );
  }

  return (
    <div className="px-lg pb-xxl md:px-xl">
      {/*
        * Explicit grid placement rather than two nested columns.
        *
        * Three blocks, two arrangements: on a phone they stack in the order somebody reads them — what
        * this is, the form, what happens after — and on a wide screen the two prose blocks stack in the
        * left column beside a form that spans both rows. Nesting the prose in one aside would give the
        * right desktop layout and the wrong phone one, with "what happens after" pushed below the fold
        * under a form nobody has filled in yet.
        */}
      <div className="mx-auto grid max-w-[1040px] gap-lg md:grid-cols-2 md:items-start md:gap-xxl">
        <Intro />

        <div className="md:col-start-2 md:row-span-2 md:row-start-1">
          <form
            onSubmit={sendCode}
            className="rounded-xl bg-card px-lg pt-lg pb-xl shadow-raised md:px-xl"
          >
            <h2 className="mb-lg text-heading">{COPY.apply.formTitle}</h2>

            {/*
              * Two short fields per row on anything wider than a small phone, one per row below that.
              *
              * `sm:` rather than always: at 360px two fields side by side leaves each about 150px, which
              * is not enough for a Persian surname and turns the value into a scroll inside the box.
              */}
            <div className="grid grid-cols-1 gap-md sm:grid-cols-2">
              <Field
                id="first-name"
                label={COPY.apply.firstName}
                value={details.firstName}
                onChange={set("firstName")}
                autoComplete="given-name"
              />
              <Field
                id="last-name"
                label={COPY.apply.lastName}
                value={details.lastName}
                onChange={set("lastName")}
                autoComplete="family-name"
              />

              {/* Its own row: it is the one control here that is not a box you type into. */}
              <div className="sm:col-span-2">
                <GenderChoice value={details.gender} onChange={set("gender")} />
              </div>

              <div>
                <label className={LABEL} htmlFor="national-id">
                  {COPY.apply.nationalId}
                </label>
                <input
                  id="national-id"
                  value={details.nationalId}
                  // Persian digits folded as they are typed, like every other number field here.
                  onChange={(e) => {
                    set("nationalId")(digitsOnly(e.target.value).slice(0, 10));
                    setBadId(false);
                  }}
                  inputMode="numeric"
                  dir="ltr"
                  className={`${FIELD} text-center tracking-[2px] ${badId ? "border-wrong" : ""}`}
                />
              </div>

              <div>
                <label className={LABEL} htmlFor="phone">
                  {COPY.apply.phone}
                </label>
                <input
                  id="phone"
                  value={details.phone}
                  onChange={(e) => {
                    set("phone")(digitsOnly(e.target.value).slice(0, 13));
                    setBadPhone(false);
                  }}
                  inputMode="numeric"
                  placeholder={COPY.enter.phonePlaceholder}
                  autoComplete="tel"
                  dir="ltr"
                  className={`${FIELD} text-center tracking-[2px] ${badPhone ? "border-wrong" : ""}`}
                />
              </div>

              {/* The school spans the row: its name is the longest thing on the form. */}
              <div className="sm:col-span-2">
                <label className={LABEL} htmlFor="school">
                  {COPY.apply.schoolName}
                </label>
                <input
                  id="school"
                  value={details.schoolName}
                  onChange={(e) => set("schoolName")(e.target.value)}
                  placeholder={COPY.apply.schoolNamePlaceholder}
                  className={FIELD}
                />
              </div>
            </div>

            {/*
              * Two lines of guidance under the whole form rather than one under each field.
              *
              * Five notes on a six-field form is more explanation than form. The id's and the phone's
              * notes become their errors when there is one — the two fields with a format to be wrong
              * about, and so the two places a per-field message earns its space.
              */}
            <p className={`mt-md text-caption ${badId ? "text-wrong" : "text-muted"}`}>
              {badId ? COPY.apply.badNationalId : COPY.apply.nationalIdNote}
            </p>
            <p className={`text-caption ${badPhone ? "text-wrong" : "text-muted"}`}>
              {badPhone ? COPY.enter.badPhone : COPY.apply.phoneNote}
            </p>

            {error}

            <button
              type="submit"
              disabled={!complete || busy}
              className={`${PRIMARY} mt-lg ${!complete || busy ? "opacity-60" : ""}`}
            >
              {busy ? COPY.enter.working : COPY.apply.next}
            </button>

            {/* The way out for somebody who is already approved and landed here by habit. */}
            <button type="button" onClick={onSignIn} className={`${QUIET} mt-md`}>
              {COPY.apply.haveAccount}
            </button>
          </form>
        </div>

        <Steps />
      </div>
    </div>
  );
}

/* ── The page's blocks ────────────────────────────────────────────────────────────────────────── */

/** Who the page is for, said before anything is asked of them. */
function Intro() {
  return (
    <header className="pt-md md:col-start-1 md:row-start-1 md:pt-xl">
      {/*
        * The page's one creature, and it sits where it decorates rather than where it would have to
        * mean something. An owl because it is the one the product already gives to the game a teacher
        * meets first, so the page is recognisably part of the same thing.
        */}
      <span className="disc-grape mb-md grid size-[72px] place-items-center rounded-pill">
        <Creature kind="owl" accent="grape" size={50} />
      </span>
      <span className="mb-md block w-fit rounded-pill bg-grape/12 px-md py-1.5 text-caption text-grape">
        {COPY.apply.eyebrow}
      </span>
      {/*
        * Fluid rather than fixed, for the reason the landing hero is: `text-hero` is 34px, which is
        * right inside a card and small for the first thing on a page. The lower bound is the number
        * that matters, because it is the one almost everybody sees.
        */}
      <h1 className="mb-md text-[clamp(28px,7.5vw,44px)] leading-[1.25] font-extrabold tracking-[-0.02em]">
        {COPY.apply.title}
      </h1>
      <p className="max-w-[420px] text-body text-muted">{COPY.apply.lead}</p>
    </header>
  );
}

/**
 * What happens after the button, as three steps on a rail.
 *
 * The same shape the landing page uses for the teaching loop, on purpose: somebody who has read that
 * page recognises it, and a numbered rail says "sequence" without a sentence having to.
 */
function Steps() {
  const steps = [COPY.apply.steps.one, COPY.apply.steps.two, COPY.apply.steps.three];
  return (
    <section className="kd-panel-close rounded-xl px-lg py-xl md:col-start-1 md:row-start-2">
      <h2 className="mb-lg text-heading">{COPY.apply.stepsTitle}</h2>
      <ol className="grid list-none gap-lg p-0">
        {steps.map((step, i) => (
          <li key={i} className="relative flex items-start gap-md">
            {/* A rail joining the markers, drawn on all but the last and only downwards from it. */}
            {i < steps.length - 1 && (
              <span
                aria-hidden
                className="absolute start-[19px] top-[42px] -bottom-lg w-0.5 rounded-full bg-grape/22"
              />
            )}
            <span className="z-1 grid size-10 shrink-0 place-items-center rounded-pill bg-card text-heading text-grape shadow-card">
              {fa(i + 1)}
            </span>
            <p className="pt-1 text-small">{step}</p>
          </li>
        ))}
      </ol>
    </section>
  );
}

/**
 * The gender question, as two buttons.
 *
 * `radiogroup` rather than two toggles: they are one answer with two values, and a screen reader that
 * meets them as independent buttons cannot say that choosing one unchooses the other. A native
 * `<select>` was the other option and is worse on the device this is filled in on — a wheel that covers
 * half the screen for a choice between two things.
 *
 * Colour is not the only signal that one is chosen. The selected pill gains a filled dot and bold text
 * as well as the accent border, because a tinted outline alone is a distinction somebody with a colour
 * vision difference has to hunt for on the one control that cannot be re-read from its value.
 */
function GenderChoice({
  value,
  onChange,
}: {
  value: Gender | null;
  onChange: (next: Gender) => void;
}) {
  return (
    <div>
      <span className={LABEL} id="gender-label">
        {COPY.apply.gender}
      </span>
      <div role="radiogroup" aria-labelledby="gender-label" className="grid grid-cols-2 gap-sm">
        <GenderOption
          option="female"
          label={COPY.apply.genderFemale}
          value={value}
          onChange={onChange}
        />
        <GenderOption
          option="male"
          label={COPY.apply.genderMale}
          value={value}
          onChange={onChange}
        />
      </div>
    </div>
  );
}

function GenderOption({
  option,
  label,
  value,
  onChange,
}: {
  option: Gender;
  label: string;
  value: Gender | null;
  onChange: (next: Gender) => void;
}) {
  const chosen = value === option;
  return (
    <button
      type="button"
      role="radio"
      aria-checked={chosen}
      onClick={() => onChange(option)}
      className={`flex h-[52px] items-center justify-center gap-sm rounded-md border-2 text-body transition-colors ${
        chosen ? "border-grape bg-grape/10 font-bold text-ink" : "border-edge bg-card text-muted"
      }`}
    >
      {/* A ring that fills, rather than a tick that appears: the empty ring is what says a choice
          is waiting to be made, on a control with no other resting state. */}
      <span
        aria-hidden
        className={`grid size-5 place-items-center rounded-pill border-2 ${
          chosen ? "border-grape" : "border-edge"
        }`}
      >
        <span className={`size-2.5 rounded-pill ${chosen ? "bg-grape" : "bg-transparent"}`} />
      </span>
      {label}
    </button>
  );
}

/* ── Small shared pieces ──────────────────────────────────────────────────────────────────────── */

/** The two later steps are one short errand each, so they get a column rather than a page. */
function Narrow({ children }: { children: React.ReactNode }) {
  return <div className="mx-auto max-w-[460px] px-lg pb-xxl md:px-xl">{children}</div>;
}

function Panel({ children }: { children: React.ReactNode }) {
  return (
    <div className="mt-md rounded-xl bg-card px-lg pt-xl pb-lg shadow-raised">{children}</div>
  );
}

/**
 * What the step is, drawn rather than lettered — an envelope, then a tick.
 *
 * A creature was here first and was the wrong instinct on these two screens. The creatures are how a
 * child is spoken to and they carry no meaning of their own; these two panels each have exactly one
 * thing to say — a message is on its way, and the thing you did worked — and a shape that says it is
 * worth more than a shape that is merely friendly. The warmth on this page is the owl above the title,
 * where it decorates instead of informing.
 *
 * The tick is mint and the envelope is grape, so the ending is visibly not another step.
 */
function StepMark({ mark }: { mark: "code" | "done" }) {
  const done = mark === "done";
  return (
    <span
      className={`grid size-[64px] place-items-center rounded-pill ${done ? "bg-mint/16" : "bg-grape/12"}`}
    >
      <svg width="30" height="30" viewBox="0 0 24 24" aria-hidden>
        {done ? (
          <path
            d="M4 12.5 9.5 18 20 6"
            fill="none"
            stroke={css(COLOR.mint)}
            strokeWidth="2.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ) : (
          <>
            <rect
              x="2.5" y="5" width="19" height="14" rx="3"
              fill="none" stroke={css(COLOR.grape)} strokeWidth="2.2"
            />
            <path
              d="M3.5 7 12 13 20.5 7"
              fill="none" stroke={css(COLOR.grape)} strokeWidth="2.2" strokeLinecap="round"
            />
          </>
        )}
      </svg>
    </span>
  );
}

/** One labelled text field. Local because only this screen lays them out in a grid. */
function Field({
  id,
  label,
  value,
  onChange,
  autoComplete,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (next: string) => void;
  autoComplete?: string;
}) {
  return (
    <div>
      <label className={LABEL} htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        {...(autoComplete ? { autoComplete } : {})}
        className={FIELD}
      />
    </div>
  );
}
